import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Award, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Resultado180 = () => {
  const navigate = useNavigate();
  const { cicloId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    fetchResultado();
  }, [cicloId]);

  const fetchResultado = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback-180/resultado/ciclo/${cicloId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setResultado(response.data);
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar resultados');
    } finally {
      setLoading(false);
    }
  };

  const getGapIcon = (gap) => {
    if (Math.abs(gap) <= 0.5) return <Minus className="h-5 w-5 text-gray-600" />;
    if (gap > 0) return <TrendingUp className="h-5 w-5 text-blue-600" />;
    return <TrendingDown className="h-5 w-5 text-orange-600" />;
  };

  const getGapColor = (gap) => {
    if (Math.abs(gap) <= 0.5) return 'bg-gray-100 text-gray-800';
    if (gap > 0) return 'bg-blue-100 text-blue-800';
    return 'bg-orange-100 text-orange-800';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!resultado) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Resultados ainda não disponíveis</p>
              <p className="text-sm text-gray-500 mt-2">
                Aguarde até que você e seu gestor concluam as avaliações
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" onClick={() => navigate('/feedback/cultural')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        
        <Button 
          onClick={() => navigate(`/feedback/180/pdi/${cicloId}`)}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Target className="h-4 w-4 mr-2" />
          Ver/Gerar PDI
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header com Médias */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Seus Resultados - Feedback 180º</CardTitle>
            <CardDescription>
              Comparação entre sua autoavaliação e a percepção do seu gestor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <p className="text-sm text-purple-700 mb-1">Sua Autoavaliação</p>
                <p className="text-3xl font-bold text-purple-900">{resultado.media_auto.toFixed(1)}</p>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-sm text-blue-700 mb-1">Avaliação do Gestor</p>
                <p className="text-3xl font-bold text-blue-900">{resultado.media_gestor.toFixed(1)}</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-700 mb-1">Diferença (Gap)</p>
                <p className={`text-3xl font-bold ${resultado.gap_medio > 0 ? 'text-blue-900' : resultado.gap_medio < 0 ? 'text-orange-900' : 'text-gray-900'}`}>
                  {resultado.gap_medio > 0 ? '+' : ''}{resultado.gap_medio.toFixed(1)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insights */}
        {resultado.insights.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-yellow-600" />
                <span>Insights Automáticos</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {resultado.insights.map((insight, index) => (
                  <div key={index} className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                    <p className="text-sm text-yellow-900">{insight}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resultados por Competência */}
        <Card>
          <CardHeader>
            <CardTitle>Análise por Competência</CardTitle>
            <CardDescription>
              Veja como suas percepções se alinham com as do seu gestor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {resultado.resultados_competencias.map((comp) => (
                <div key={comp.competencia_id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{comp.nome}</h4>
                      <p className="text-sm text-gray-600 mt-1">{comp.interpretacao}</p>
                    </div>
                    
                    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getGapColor(comp.gap)}`}>
                      {getGapIcon(comp.gap)}
                      <span className="text-sm font-medium">
                        {comp.gap > 0 ? '+' : ''}{comp.gap.toFixed(1)}
                      </span>
                    </div>
                  </div>

                  {/* Barra de comparação */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-600 w-16">Você:</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-purple-600 h-full rounded-full transition-all"
                          style={{ width: `${(comp.nota_auto / 5) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-purple-900 w-8">
                        {comp.nota_auto}
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-600 w-16">Gestor:</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all"
                          style={{ width: `${(comp.nota_gestor / 5) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-blue-900 w-8">
                        {comp.nota_gestor}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Competências Alinhadas/Desalinhadas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-green-700">Competências Alinhadas</CardTitle>
              <CardDescription>Percepções semelhantes (gap ≤ 0.5)</CardDescription>
            </CardHeader>
            <CardContent>
              {resultado.competencias_alinhadas.length > 0 ? (
                <ul className="space-y-2">
                  {resultado.competencias_alinhadas.map((nome, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>{nome}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">Nenhuma competência totalmente alinhada</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-orange-700">Necessitam Alinhamento</CardTitle>
              <CardDescription>Maiores diferenças de percepção (gap &gt; 1)</CardDescription>
            </CardHeader>
            <CardContent>
              {resultado.competencias_desalinhadas.length > 0 ? (
                <ul className="space-y-2">
                  {resultado.competencias_desalinhadas.map((nome, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>{nome}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">Todas as competências bem alinhadas!</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Resultado180;
